// Централизованная конфигурация API
export const getApiBaseUrl = (): string => {
  // Если мы в браузере, используем текущий протокол и хост
  if (typeof window !== 'undefined') {
    const { protocol, hostname, port } = window.location;
    
    // В разработке на localhost используем порт 12000
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return `${protocol}//${hostname}:12000`;
    }
    
    // В продакшене используем тот же хост с правильным протоколом
    // ПРИНУДИТЕЛЬНО используем HTTPS в продакшене
    const finalProtocol = hostname === 'ruton.vip' ? 'https:' : protocol;
    return `${finalProtocol}//${hostname}${port ? `:${port}` : ''}`;
  }
  
  // Fallback для SSR или других случаев
  return process.env.NODE_ENV === 'production' ? '' : 'http://localhost:12000';
};

// Интерфейсы для типизации
export interface ApiResponse<T = any> {
  ok?: boolean;
  success?: boolean;
  data?: T;
  error?: string;
  detail?: string;
}

export interface ApiError {
  detail?: string;
  error?: string;
  message?: string;
}

// Базовый класс для работы с API
class BaseAPI {
  protected getBaseUrl: () => string;

  constructor(baseUrlFn: () => string = getApiBaseUrl) {
    this.getBaseUrl = baseUrlFn;
  }

  protected async makeRequest<T>(
    endpoint: string, 
    options: RequestInit = {}
  ): Promise<T> {
    const baseUrl = this.getBaseUrl();
    const url = `${baseUrl}${endpoint}`;
    
    const defaultOptions: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        ...options.headers,
      },
      credentials: 'include', // Важно для работы с cookies
      ...options,
    };

    try {
      const response = await fetch(url, defaultOptions);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ 
          detail: `HTTP error! status: ${response.status}` 
        }));
        const error = new Error(errorData.detail || errorData.error || `HTTP ${response.status}`);
        (error as any).status = response.status;
        (error as any).response = response;
        throw error;
      }

      return response.json();
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Network error');
    }
  }

  // Специальный метод для auth запросов, который не выбрасывает ошибки для 401
  protected async makeAuthRequest<T>(
    endpoint: string, 
    options: RequestInit = {}
  ): Promise<{ success: boolean; data?: T; status?: number; error?: string }> {
    const baseUrl = this.getBaseUrl();
    const url = `${baseUrl}${endpoint}`;
    
    const defaultOptions: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        ...options.headers,
      },
      credentials: 'include',
      ...options,
    };

    try {
      const response = await fetch(url, defaultOptions);
      
      if (response.ok) {
        const data = await response.json();
        return { success: true, data };
      }
      
      // Для не-успешных ответов не выбрасываем ошибку, а возвращаем структурированный результат
      const errorData = await response.json().catch(() => ({ 
        detail: `HTTP error! status: ${response.status}` 
      }));
      
      return {
        success: false,
        status: response.status,
        error: errorData.detail || errorData.error || `HTTP ${response.status}`
      };
    } catch (error) {
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Network error' 
      };
    }
  }

  protected async makeFormRequest<T>(
    endpoint: string,
    formData: FormData,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.getBaseUrl()}${endpoint}`;
    
    const defaultOptions: RequestInit = {
      method: 'POST',
      body: formData,
      credentials: 'include',
      ...options,
      // Не устанавливаем Content-Type для FormData - браузер сделает это автоматически
      headers: {
        'Accept': 'application/json',
        ...options.headers,
      },
    };

    try {
      const response = await fetch(url, defaultOptions);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ 
          detail: `HTTP error! status: ${response.status}` 
        }));
        throw new Error(errorData.detail || errorData.error || `HTTP ${response.status}`);
      }

      return response.json();
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Network error');
    }
  }
}

// Экспортируем базовый класс для использования в других API модулях
export { BaseAPI };

// Утилитарные функции для обработки ответов
export const handleApiResponse = <T>(response: ApiResponse<T>): T => {
  if (response.ok === false || response.success === false) {
    throw new Error(response.error || response.detail || 'API Error');
  }
  
  if (response.data !== undefined) {
    return response.data;
  }
  
  return response as T;
};

export const handleApiError = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message;
  }
  
  if (typeof error === 'object' && error !== null) {
    const apiError = error as ApiError;
    return apiError.detail || apiError.error || apiError.message || 'Unknown error';
  }
  
  return 'Unknown error';
};
